/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { ExtendedPaginationBox } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { SidebarSelector } from "~/components/SidebarWithMultiSelect/SidebarSelector";
import { useEstateRecords } from "~/hooks";
import { Option } from "~/models";
import { isCloudResource, noop } from "~/tools";
import { getSelectedSecurityGroups } from "./utils";

type Props = {
  onSubmit: (value: string[], removeItems: string[]) => void;
  onClose: () => void;
  isOpen: boolean;
  excludeItems?: string[];
};

type Filter = {
  page: number;
  size: number;
  search: string;
};

const FETCH_LIMIT = 20;

const initialFilter = {
  page: 0,
  size: FETCH_LIMIT,
  search: "",
};

export function AwsSecurityGroupSelector({
  onSubmit,
  onClose,
  isOpen,
  excludeItems,
}: Props) {
  const [options, setOptions] = useState<Option[]>([]);
  const [selected, setSelected] = useState<string[]>(
    getSelectedSecurityGroups(excludeItems ?? [])
  );
  const [removed, setRemoved] = useState<string[]>([]);

  const [filter, setFilter] = useState<Filter>(initialFilter);

  useEffect(() => {
    setSelected(
      getSelectedSecurityGroups([...selected, ...(excludeItems ?? [])])
    );
  }, [excludeItems, options]);

  const { data, loading } = useEstateRecords(
    {
      variables: {
        page: filter.page,
        limit: FETCH_LIMIT,
        query: filter.search,
        filter: {
          provider: [Provider.Aws],
          typeSubtype: ["ec2/security-group"],
        },
      },
    },
    noop
  );

  useEffect(() => {
    if (data) {
      const resources = data?.resources ?? [];
      const selectorOptions: Option[] = resources
        .filter(isCloudResource)
        .map((resource) => {
          return {
            label: resource.name,
            value: resource.name,
          };
        });

      setOptions(selectorOptions);
      setFilter((prevFilter) => ({
        ...prevFilter,
        size: data.count ?? 0,
      }));
    }
  }, [data]);

  const onApply = (item: string) => {
    const newSelectedItems = [...(selected ?? [])];
    const indexOfItem = newSelectedItems.indexOf(item);

    if (indexOfItem === -1) {
      newSelectedItems.push(item);
    } else {
      newSelectedItems.splice(indexOfItem, 1);
      setRemoved([...removed, item]);
    }

    setSelected(newSelectedItems);
  };

  const handleSubmit = () => {
    onSubmit(selected, removed);
    handleClose();
  };

  const onPageChange = (page: number) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      page: Math.ceil(page / FETCH_LIMIT),
    }));
  };

  const handleClose = () => {
    setFilter(initialFilter);
    onClose();
  };

  return (
    <SidebarSelector
      name="AWS Security Group"
      pluralName="Select AWS Security Groups"
      isOpen={isOpen}
      options={options}
      selectedOptions={selected}
      removedSelections={removed}
      showLoader={loading}
      searchFilter={filter.search}
      selectButtonLabel="Apply"
      setSearchFilter={(value) =>
        setFilter((prevFilter) => ({
          ...prevFilter,
          search: value,
          page: 0,
        }))
      }
      footer={
        <ExtendedPaginationBox
          sidebar
          small
          count={filter.size}
          from={filter.page * FETCH_LIMIT}
          setPage={onPageChange}
          setSize={noop}
          size={FETCH_LIMIT}
        />
      }
      onClick={handleClose}
      onSubmit={onApply}
      onMultiSubmit={handleSubmit}
    />
  );
}
